import Phaser from 'phaser';
import { SceneKeys } from '../game/Constants';
import { GameState } from '../game/GameState';
import { AssetRegistry } from '../systems/AssetRegistry';
import { DataRegistry } from '../systems/DataRegistry';
import { DiplomacyManager } from '../systems/DiplomacyManager';
import { EspionageManager } from '../systems/EspionageManager';
import type { EspionageOperationData } from '../types/data';
import { VisualTheme } from '../ui/VisualTheme';
import { createTextButton } from '../ui/components/UIButton';
import { addBackButton, drawPanel, drawScreenBackground } from '../ui/components/SceneChrome';

const TABS = ['Networks', 'Operations', 'Counter-Intelligence', 'Chronicle'] as const;
type EspionageTab = typeof TABS[number];

export class EspionageScene extends Phaser.Scene {
  private activeTab: EspionageTab = 'Networks';
  private targetIndex = 0;
  private selectedOperationIndex = 0;

  constructor() {
    super(SceneKeys.Espionage);
  }

  create() {
    EspionageManager.instance.ensureCampaignInitialized();
    this.render();
  }

  private render() {
    this.children.removeAll();
    EspionageManager.instance.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const player = campaign.playerFactionId;
    const targets = DataRegistry.instance.factionList().filter((faction) => faction.id !== player);
    this.targetIndex = Phaser.Math.Clamp(this.targetIndex, 0, Math.max(0, targets.length - 1));
    const target = targets[this.targetIndex];
    if (!target) return;

    drawScreenBackground(this, 'The Shadow Council', 'Build networks, assess risk, penetrate rival courts and defend the realm against covert attack');
    const backdrop = AssetRegistry.instance.image('background_shadow_council', 'fallback.art');
    this.add.image(800, 500, backdrop).setDisplaySize(1600, 900).setAlpha(0.3).setDepth(-2);
    this.add.rectangle(800, 500, 1550, 735, VisualTheme.colors.ink, 0.75).setDepth(-1);
    addBackButton(this);
    this.drawTabs();
    this.drawTargetRail(targets, target.id);
    this.drawTargetSummary(target.id);

    if (this.activeTab === 'Networks') this.drawNetworks(target.id);
    else if (this.activeTab === 'Operations') this.drawOperations(target.id);
    else if (this.activeTab === 'Counter-Intelligence') this.drawCounterIntelligence();
    else this.drawChronicle();

    const network = EspionageManager.instance.network(player, target.id, false);
    const exposed = window as unknown as { render_game_to_text?: () => string; advanceTime?: (ms: number) => void };
    exposed.render_game_to_text = () => JSON.stringify({
      mode: 'espionage',
      tab: this.activeTab,
      target: target.id,
      network: network ? { strength: network.strength, infiltration: network.infiltration, heat: network.heat, discovered: network.discovered } : undefined,
      counterIntelligence: campaign.factions[player].counterIntelligence,
      activeOperations: campaign.espionageMissions.filter((mission) => mission.sourceFactionId === player),
      history: campaign.espionageHistory.slice(0, 8),
    });
    exposed.advanceTime = () => undefined;
  }

  private drawTabs() {
    TABS.forEach((tab, index) => createTextButton(this, {
      label: tab,
      x: 470 + index * 265,
      y: 112,
      width: 238,
      height: 38,
      fontSize: 13,
      onClick: () => { this.activeTab = tab; this.render(); },
    }));
    const selected = TABS.indexOf(this.activeTab);
    this.add.rectangle(470 + selected * 265, 134, 205, 3, VisualTheme.colors.goldBright, 1);
  }

  private drawTargetRail(targets: ReturnType<DataRegistry['factionList']>, selectedId: string) {
    const campaign = GameState.instance.requireCampaign();
    const player = campaign.playerFactionId;
    drawPanel(this, 240, 525, 390, 660, 'Foreign Courts');
    targets.slice(0, 12).forEach((faction, index) => {
      const y = 230 + index * 48;
      const selected = faction.id === selectedId;
      const network = EspionageManager.instance.network(player, faction.id, false);
      const relation = DiplomacyManager.instance.relation(player, faction.id);
      const accent = Phaser.Display.Color.HexStringToColor(faction.colors.primary).color;
      this.add.rectangle(238, y, 340, 40, selected ? VisualTheme.colors.panelHover : VisualTheme.colors.slate, selected ? 0.98 : 0.82)
        .setStrokeStyle(selected ? 2 : 1, selected ? VisualTheme.colors.goldBright : accent, selected ? 1 : 0.55);
      this.add.circle(88, y, 7, accent, 1).setStrokeStyle(1, VisualTheme.colors.parchment, 0.7);
      this.add.text(104, y - 14, faction.name, { fontFamily: VisualTheme.fonts.display, fontSize: '13px', color: VisualTheme.text.title });
      this.add.text(104, y + 5, network ? `Network ${Math.round(network.strength)}  •  Heat ${Math.round(network.heat)}` : 'No network', { fontFamily: VisualTheme.fonts.body, fontSize: '9px', color: network?.discovered ? VisualTheme.text.danger : VisualTheme.text.muted });
      this.add.text(332, y - 7, `${Math.round(relation?.trust ?? 0)}`, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: (relation?.trust ?? 0) >= 20 ? '#9ed6b3' : (relation?.trust ?? 0) <= -20 ? '#e78b82' : VisualTheme.text.body }).setOrigin(1, 0);
      const hit = this.add.rectangle(238, y, 340, 40, 0xffffff, 0).setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => { this.targetIndex = index; this.selectedOperationIndex = 0; this.render(); });
    });
  }

  private drawTargetSummary(targetId: string) {
    const campaign = GameState.instance.requireCampaign();
    const player = campaign.playerFactionId;
    const target = campaign.factions[targetId];
    const data = DataRegistry.instance.factions[targetId];
    const network = EspionageManager.instance.network(player, targetId, false);
    drawPanel(this, 1000, 210, 1080, 118, data.name);
    this.add.text(495, 183, `${target.primaryCulture}  •  ${target.stateReligion}  •  ${data.aiPersonality.replaceAll('_', ' ')}`, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.accent });
    this.add.text(495, 208, `Target counter-intelligence ${Math.round(target.counterIntelligence)}  •  Prestige ${Math.round(target.prestige)}  •  Legitimacy ${Math.round(target.legitimacy)}  •  Network ${Math.round(network?.strength ?? 0)}`, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.body });
    if (network?.discovered) this.add.text(1325, 183, 'NETWORK COMPROMISED', { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: VisualTheme.text.danger });
  }

  private drawNetworks(targetId: string) {
    const campaign = GameState.instance.requireCampaign();
    const player = campaign.playerFactionId;
    const target = DataRegistry.instance.factions[targetId];
    const network = EspionageManager.instance.network(player, targetId, false);
    drawPanel(this, 790, 535, 630, 540, `${target.name} Network`);
    drawPanel(this, 1275, 535, 300, 540, 'Active Cells');

    const values = [
      ['STRENGTH', network?.strength ?? 0, 0x4f7892, 'Agents, safe houses and access'],
      ['INFILTRATION', network?.infiltration ?? 0, 0x4b9b70, 'Depth inside state institutions'],
      ['HEAT', network?.heat ?? 0, 0xa64b4f, 'Suspicion and exposure pressure'],
      ['TARGET DEFENSE', campaign.factions[targetId].counterIntelligence, 0xc79b4b, 'Capacity to detect foreign cells'],
    ] as const;
    values.forEach(([label, value, color, description], index) => {
      const y = 330 + index * 105;
      this.add.text(510, y - 25, label, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: VisualTheme.text.muted });
      this.add.text(1010, y - 25, `${Math.round(value)}`, { fontFamily: VisualTheme.fonts.display, fontSize: '18px', color: this.hex(color) }).setOrigin(1, 0);
      this.drawProgressBar(510, y + 2, 500, value, color);
      this.add.text(510, y + 19, description, { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: VisualTheme.text.body });
    });

    createTextButton(this, {
      label: network ? 'Expand Network (35 gold)' : 'Establish Network (30 gold)',
      x: 790,
      y: 765,
      width: 480,
      height: 42,
      fontSize: 13,
      onClick: () => {
        if (network) EspionageManager.instance.investNetwork(player, targetId);
        else EspionageManager.instance.establishNetwork(player, targetId);
        this.render();
      },
    });

    const active = campaign.espionageMissions.filter((mission) => mission.sourceFactionId === player);
    if (!active.length) this.add.text(1160, 330, 'No covert operations are currently active.', { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.muted, wordWrap: { width: 230 } });
    active.slice(0, 6).forEach((mission, index) => {
      const operation = EspionageManager.instance.operation(mission.actionId);
      const y = 330 + index * 78;
      this.add.rectangle(1275, y + 18, 260, 65, VisualTheme.colors.slate, 0.88).setStrokeStyle(1, mission.discovered ? 0xa64b4f : 0x4f7892, 0.75);
      this.add.text(1160, y - 4, operation?.name ?? mission.actionId, { fontFamily: VisualTheme.fonts.display, fontSize: '12px', color: VisualTheme.text.title, wordWrap: { width: 210 } });
      this.add.text(1160, y + 31, `${DataRegistry.instance.factions[mission.targetFactionId]?.name ?? mission.targetFactionId}  •  ${mission.remainingMonths}m`, { fontFamily: VisualTheme.fonts.body, fontSize: '9px', color: VisualTheme.text.muted });
    });
  }

  private drawOperations(targetId: string) {
    const campaign = GameState.instance.requireCampaign();
    const player = campaign.playerFactionId;
    const network = EspionageManager.instance.network(player, targetId, false);
    const operations = DataRegistry.instance.espionageOperationList();
    this.selectedOperationIndex = Phaser.Math.Clamp(this.selectedOperationIndex, 0, Math.max(0, operations.length - 1));
    const selected = operations[this.selectedOperationIndex];
    drawPanel(this, 810, 535, 670, 540, 'Covert Operations');
    drawPanel(this, 1310, 535, 330, 540, selected?.name ?? 'Operation Brief');

    operations.slice(0, 10).forEach((operation, index) => {
      const x = 500 + (index % 2) * 320;
      const y = 285 + Math.floor(index / 2) * 96;
      const available = this.operationAvailable(operation, targetId);
      const selectedRow = index === this.selectedOperationIndex;
      const color = available ? this.categoryColor(operation.category) : 0x5d5660;
      this.add.rectangle(x + 145, y + 34, 295, 82, selectedRow ? VisualTheme.colors.panelHover : VisualTheme.colors.slate, 0.93).setStrokeStyle(selectedRow ? 2 : 1, color, selectedRow ? 1 : 0.62);
      this.add.text(x + 10, y + 2, operation.name, { fontFamily: VisualTheme.fonts.display, fontSize: '14px', color: VisualTheme.text.title, wordWrap: { width: 245 } });
      this.add.text(x + 10, y + 28, `${operation.category.toUpperCase()}  •  ${operation.duration}m  •  ${operation.cost} gold`, { fontFamily: VisualTheme.fonts.body, fontSize: '9px', color: VisualTheme.text.accent });
      this.add.text(x + 10, y + 50, `Network ${operation.networkRequired}  •  Detection ${operation.detectionRisk}%`, { fontFamily: VisualTheme.fonts.body, fontSize: '9px', color: available ? VisualTheme.text.body : '#ba9292' });
      const hit = this.add.rectangle(x + 145, y + 34, 295, 82, 0xffffff, 0).setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => { this.selectedOperationIndex = index; this.render(); });
    });

    if (!selected) return;
    const source = campaign.factions[player];
    const target = campaign.factions[targetId];
    const modifiers = source ? source.counterIntelligence : 0;
    const projectedSuccess = Phaser.Math.Clamp(selected.baseSuccess + (network?.strength ?? 0) * 0.12 + (network?.infiltration ?? 0) * 0.24 - target.counterIntelligence * 0.22, 5, 95);
    const projectedDetection = Phaser.Math.Clamp(selected.detectionRisk + (network?.heat ?? 0) * 0.28 + target.counterIntelligence * 0.2 - modifiers * 0.05, 4, 95);
    this.add.text(1165, 285, selected.description, { fontFamily: VisualTheme.fonts.body, fontSize: '13px', color: VisualTheme.text.body, wordWrap: { width: 285 }, lineSpacing: 5 });
    this.add.text(1165, 410, `SUCCESS EFFECT\n${selected.successEffect}`, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: '#9ed6b3', wordWrap: { width: 285 }, lineSpacing: 4 });
    this.add.text(1165, 500, `FAILURE / EXPOSURE\n${selected.failureEffect}`, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: '#e7a59f', wordWrap: { width: 285 }, lineSpacing: 4 });
    this.add.text(1165, 600, `Projected success ${Math.round(projectedSuccess)}%\nProjected detection ${Math.round(projectedDetection)}%\nNetwork consumption ${selected.networkCost}`, { fontFamily: VisualTheme.fonts.display, fontSize: '14px', color: VisualTheme.text.title, lineSpacing: 8 });
    const activeAgainstTarget = campaign.espionageMissions.find((mission) => mission.sourceFactionId === player && mission.targetFactionId === targetId && mission.status === 'active');
    const available = this.operationAvailable(selected, targetId) && !activeAgainstTarget;
    createTextButton(this, {
      label: activeAgainstTarget ? 'Operation Already Active' : available ? 'Authorize Operation' : this.operationBlockReason(selected, targetId),
      x: 1310,
      y: 755,
      width: 300,
      height: 42,
      fontSize: 11,
      onClick: () => { if (available) EspionageManager.instance.startMission(player, targetId, selected.id); this.render(); },
    });
  }

  private drawCounterIntelligence() {
    const campaign = GameState.instance.requireCampaign();
    const player = campaign.playerFactionId;
    const faction = campaign.factions[player];
    const hostile = Object.values(campaign.spyNetworks).filter((network) => network.targetFactionId === player);
    drawPanel(this, 780, 535, 610, 540, 'Counter-Intelligence Directorate');
    drawPanel(this, 1280, 535, 350, 540, 'Detected Threats');
    this.add.text(510, 300, `${Math.round(faction.counterIntelligence)}`, { fontFamily: VisualTheme.fonts.display, fontSize: '64px', color: '#f0d78a' });
    this.add.text(510, 375, 'COUNTER-INTELLIGENCE CAPACITY', { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.muted });
    this.drawProgressBar(510, 410, 500, faction.counterIntelligence, 0xc79b4b);
    this.add.text(510, 445, 'Higher capacity raises foreign detection risk, uncovers hot networks and protects institutions, markets and the ruling house.', { fontFamily: VisualTheme.fonts.body, fontSize: '13px', color: VisualTheme.text.body, wordWrap: { width: 500 }, lineSpacing: 5 });
    createTextButton(this, {
      label: 'Reform Directorate (50 gold)',
      x: 780,
      y: 605,
      width: 460,
      height: 42,
      fontSize: 13,
      onClick: () => { EspionageManager.instance.strengthenCounterIntelligence(player); this.render(); },
    });
    const incoming = campaign.espionageHistory.filter((entry) => entry.targetFactionId === player).slice(0, 3);
    this.add.text(510, 680, `Known incidents: ${incoming.length}  •  Suspected foreign networks: ${hostile.filter((network) => network.discovered).length}`, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.accent });

    if (!hostile.length) this.add.text(1130, 320, 'No foreign network has been identified. Absence of evidence is not evidence of safety.', { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.muted, wordWrap: { width: 290 } });
    hostile.slice(0, 6).forEach((network, index) => {
      const y = 320 + index * 75;
      const revealed = network.discovered;
      this.add.rectangle(1280, y + 20, 300, 62, VisualTheme.colors.slate, 0.88).setStrokeStyle(1, revealed ? 0xa64b4f : 0x5d5660, 0.75);
      this.add.text(1145, y - 2, revealed ? DataRegistry.instance.factions[network.sourceFactionId]?.name ?? network.sourceFactionId : 'Unknown foreign hand', { fontFamily: VisualTheme.fonts.display, fontSize: '13px', color: revealed ? '#e7a59f' : VisualTheme.text.title });
      this.add.text(1145, y + 28, revealed ? `Strength ${Math.round(network.strength)}  •  Heat ${Math.round(network.heat)}` : `Suspicion ${Math.round(network.heat)}  •  identity concealed`, { fontFamily: VisualTheme.fonts.body, fontSize: '9px', color: VisualTheme.text.muted });
    });
  }

  private drawChronicle() {
    const campaign = GameState.instance.requireCampaign();
    drawPanel(this, 950, 535, 950, 540, 'Intelligence Chronicle');
    drawPanel(this, 1335, 535, 280, 540, 'Operational Record');
    const history = campaign.espionageHistory.slice(0, 10);
    if (!history.length) this.add.text(510, 320, 'The intelligence archive contains no completed operations yet.', { fontFamily: VisualTheme.fonts.body, fontSize: '13px', color: VisualTheme.text.muted });
    history.forEach((entry, index) => {
      const y = 280 + index * 52;
      const operation = EspionageManager.instance.operation(entry.actionId);
      const color = entry.outcome === 'success' ? 0x4b9b70 : entry.outcome === 'exposed' ? 0xa64b4f : entry.outcome === 'cancelled' ? 0x7b7580 : 0xb06b4f;
      this.add.rectangle(940, y + 18, 840, 45, VisualTheme.colors.slate, 0.88).setStrokeStyle(1, color, 0.65);
      this.add.text(535, y + 1, `TURN ${entry.turn}  •  ${(operation?.name ?? entry.actionId).toUpperCase()}`, { fontFamily: VisualTheme.fonts.body, fontSize: '9px', color: this.hex(color) });
      this.add.text(535, y + 19, entry.description, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: VisualTheme.text.body, wordWrap: { width: 640 } });
      this.add.text(1350, y + 9, entry.outcome.toUpperCase(), { fontFamily: VisualTheme.fonts.display, fontSize: '11px', color: this.hex(color) }).setOrigin(1, 0);
    });
    const player = campaign.playerFactionId;
    const successes = campaign.espionageHistory.filter((entry) => entry.sourceFactionId === player && entry.outcome === 'success').length;
    const failures = campaign.espionageHistory.filter((entry) => entry.sourceFactionId === player && entry.outcome === 'failure').length;
    const exposed = campaign.espionageHistory.filter((entry) => entry.sourceFactionId === player && entry.outcome === 'exposed').length;
    this.add.text(1220, 330, `SUCCESSFUL\n${successes}\n\nFAILED\n${failures}\n\nEXPOSED\n${exposed}`, { fontFamily: VisualTheme.fonts.display, fontSize: '17px', color: VisualTheme.text.title, lineSpacing: 8 });
  }

  private operationAvailable(operation: EspionageOperationData, targetId: string) {
    const campaign = GameState.instance.requireCampaign();
    const player = campaign.playerFactionId;
    const faction = campaign.factions[player];
    const network = EspionageManager.instance.network(player, targetId, false);
    return Boolean(faction && network && faction.treasury >= operation.cost && network.strength >= operation.networkRequired && (!operation.requiredTech || faction.researchedTechs.includes(operation.requiredTech)));
  }

  private operationBlockReason(operation: EspionageOperationData, targetId: string) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    const network = EspionageManager.instance.network(campaign.playerFactionId, targetId, false);
    if (!network) return 'Establish Network First';
    if (network.strength < operation.networkRequired) return `Need Network ${operation.networkRequired}`;
    if (operation.requiredTech && !faction.researchedTechs.includes(operation.requiredTech)) return `Need ${DataRegistry.instance.technologies[operation.requiredTech]?.name ?? 'Technology'}`;
    if (faction.treasury < operation.cost) return `Need ${operation.cost} Gold`;
    return 'Operation Unavailable';
  }

  private categoryColor(category: string) {
    if (category === 'intelligence') return 0x4f7892;
    if (category === 'diplomatic') return 0x8f6fb0;
    if (category === 'economic') return 0xc79b4b;
    if (category === 'military') return 0xa64b4f;
    if (category === 'dynastic') return 0x9a6b82;
    return 0x6b8d67;
  }

  private drawProgressBar(x: number, y: number, width: number, value: number, color: number) {
    this.add.rectangle(x, y, width, 12, VisualTheme.colors.ink, 0.92).setOrigin(0).setStrokeStyle(1, VisualTheme.colors.gold, 0.4);
    this.add.rectangle(x + 2, y + 2, Math.max(0, width - 4) * Phaser.Math.Clamp(value / 100, 0, 1), 8, color, 0.95).setOrigin(0);
  }

  private hex(color: number) { return `#${color.toString(16).padStart(6, '0')}`; }
}